<template>
   <div class="py-10 px-4 sm:px-6 lg:px-24">
      <main class="flex flex-col gap-8">
         <!-- Page Header and Date Filters -->
         <div class="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-gray-150 shadow-sm">
            <div>
               <h1 class="text-xl font-bold text-gray-900">Financial Reports</h1>
               <p class="text-xs font-semibold text-gray-500 mt-1">Generate sales and payment reports with direct CSV downloads</p>
            </div>
            
            <div class="flex flex-wrap gap-4 items-center justify-end">
               <!-- Dropdown Scope Filter -->
               <div class="relative flex items-center gap-2">
                  <span class="text-xs font-bold text-gray-400 whitespace-nowrap uppercase tracking-wider">Date Period:</span>
                  <div class="relative min-w-[180px]">
                     <select v-model="selectedScope" @change="fetchReports" class="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent appearance-none transition-all duration-300 cursor-pointer font-semibold text-gray-700">
                        <option v-for="scope in scopes" :key="scope.id" :value="scope.id">{{ scope.name }}</option>
                     </select>
                     <span class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-gray-400">
                        <font-awesome-icon icon="fa-solid fa-chevron-down" class="text-xs" />
                     </span>
                  </div>
               </div>

               <!-- Custom date range inputs -->
               <div v-if="selectedScope === 'custom'" class="flex items-center gap-2">
                  <input type="date" v-model="startDate" @change="fetchReports" class="border border-gray-300 rounded-lg px-3 py-1.5 text-sm outline-none font-semibold text-gray-700 focus:ring-2 focus:ring-indigo-500 focus:border-transparent">
                  <span class="text-gray-400 text-sm font-semibold">to</span>
                  <input type="date" v-model="endDate" @change="fetchReports" class="border border-gray-300 rounded-lg px-3 py-1.5 text-sm outline-none font-semibold text-gray-700 focus:ring-2 focus:ring-indigo-500 focus:border-transparent">
               </div>
            </div>
         </div>

         <!-- Cards Section -->
         <div class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <!-- 1. Total Booking Sales Card -->
            <div class="group relative overflow-hidden rounded-2xl bg-white p-6 border border-gray-150 shadow-sm hover:shadow-md hover:border-indigo-200 transition-all duration-300 flex flex-col justify-between h-44">
               <div class="flex items-center justify-between">
                  <div>
                     <p class="text-xs font-extrabold text-gray-400 uppercase tracking-wider">Booking Sales</p>
                     <p class="text-2xl font-black text-gray-900 mt-2">
                        {{ sales.count }} Bookings
                     </p>
                     <p class="text-xl font-extrabold text-indigo-600 mt-0.5">
                        ${{ Number(sales.sum).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                     </p>
                  </div>
                  <div class="rounded-xl bg-indigo-50 text-indigo-600 p-3.5 group-hover:scale-110 transition-all duration-300">
                     <font-awesome-icon icon="fa-solid fa-wallet" class="text-lg" />
                  </div>
               </div>
               <div class="border-t border-gray-100 pt-3 flex items-center justify-between">
                  <button @click="downloadCsv('sales')" :disabled="isDownloading" class="text-xs font-extrabold text-indigo-600 hover:text-indigo-800 flex items-center gap-1 transition-colors">
                     <font-awesome-icon icon="fa-solid fa-download" class="text-[10px]" /> Download CSV Details
                  </button>
               </div>
            </div>

            <!-- 2. Paid Bookings Card -->
            <div class="group relative overflow-hidden rounded-2xl bg-white p-6 border border-gray-150 shadow-sm hover:shadow-md hover:border-emerald-200 transition-all duration-300 flex flex-col justify-between h-44">
               <div class="flex items-center justify-between">
                  <div>
                     <p class="text-xs font-extrabold text-gray-400 uppercase tracking-wider">Paid Bookings</p>
                     <p class="text-2xl font-black text-gray-900 mt-2">
                        {{ paid.count }} Bookings
                     </p>
                     <p class="text-xl font-extrabold text-emerald-600 mt-0.5">
                        ${{ Number(paid.sum).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                     </p>
                  </div>
                  <div class="rounded-xl bg-emerald-50 text-emerald-600 p-3.5 group-hover:scale-110 transition-all duration-300">
                     <font-awesome-icon icon="fa-solid fa-circle-check" class="text-lg" />
                  </div>
               </div>
               <div class="border-t border-gray-100 pt-3 flex items-center justify-between">
                  <button @click="downloadCsv('paid')" :disabled="isDownloading" class="text-xs font-extrabold text-emerald-600 hover:text-emerald-800 flex items-center gap-1 transition-colors">
                     <font-awesome-icon icon="fa-solid fa-download" class="text-[10px]" /> Download CSV Details
                  </button>
               </div>
            </div>

            <!-- 3. Unpaid Bookings Card -->
            <div class="group relative overflow-hidden rounded-2xl bg-white p-6 border border-gray-150 shadow-sm hover:shadow-md hover:border-amber-200 transition-all duration-300 flex flex-col justify-between h-44">
               <div class="flex items-center justify-between">
                  <div>
                     <p class="text-xs font-extrabold text-gray-400 uppercase tracking-wider">Unpaid Bookings</p>
                     <p class="text-2xl font-black text-gray-900 mt-2">
                        {{ unpaid.count }} Bookings
                     </p>
                     <p class="text-xl font-extrabold text-amber-600 mt-0.5">
                        ${{ Number(unpaid.sum).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                     </p>
                  </div>
                  <div class="rounded-xl bg-amber-50 text-amber-600 p-3.5 group-hover:scale-110 transition-all duration-300">
                     <font-awesome-icon icon="fa-solid fa-circle-xmark" class="text-lg" />
                  </div>
               </div>
               <div class="border-t border-gray-100 pt-3 flex items-center justify-between">
                  <button @click="downloadCsv('unpaid')" :disabled="isDownloading" class="text-xs font-extrabold text-amber-600 hover:text-amber-800 flex items-center gap-1 transition-colors">
                     <font-awesome-icon icon="fa-solid fa-download" class="text-[10px]" /> Download CSV Details
                  </button>
               </div>
            </div>
         </div>

         <!-- Staff Performance Section (Agency Admin Only) -->
         <div v-if="isAgencyAdmin" class="mt-4">
            <div class="mb-4">
               <h3 class="text-base font-bold text-gray-900">Staff Performance Reports</h3>
               <p class="text-xs font-semibold text-gray-500 mt-0.5">View booking totals per staff member and download detailed CSV performance reports</p>
            </div>
            
            <div class="flow-root">
               <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                  <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                     <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                        <table class="min-w-full divide-y divide-gray-300">
                           <thead class="bg-gray-50">
                              <tr>
                                 <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Staff Member</th>
                                 <th scope="col" class="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">Confirmed Bookings</th>
                                 <th scope="col" class="px-3 py-3.5 text-center text-sm font-semibold text-gray-900">Total Sales Value</th>
                                 <th scope="col" class="relative py-3.5 pl-3 pr-4 text-right text-sm font-semibold text-gray-900 sm:pr-6">Report Download</th>
                              </tr>
                           </thead>
                           <tbody class="divide-y divide-gray-200 bg-white">
                              <tr v-for="staff in staffBreakdown" :key="staff.agent_id">
                                 <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-semibold text-gray-900 sm:pl-6 capitalize">
                                    {{ staff.agent_name }}
                                 </td>
                                 <td class="whitespace-nowrap px-3 py-4 text-sm text-center text-gray-500">
                                    {{ staff.count }}
                                 </td>
                                 <td class="whitespace-nowrap px-3 py-4 text-sm text-center text-indigo-600 font-bold">
                                    ${{ Number(staff.sum).toFixed(2) }}
                                 </td>
                                 <td class="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-semibold sm:pr-6">
                                    <button @click="downloadCsv('staff', staff.agent_id, staff.agent_name)" :disabled="isDownloading" class="text-indigo-600 hover:text-indigo-900 flex items-center gap-1 ml-auto">
                                       <font-awesome-icon icon="fa-solid fa-download" class="text-[10px]" /> Download CSV
                                    </button>
                                 </td>
                              </tr>
                              <tr v-if="staffBreakdown && staffBreakdown.length === 0">
                                 <td colspan="4" class="text-center whitespace-nowrap py-8 pl-4 pr-3 text-sm font-medium text-gray-500 sm:pl-6">
                                    No staff members registered.
                                 </td>
                              </tr>
                           </tbody>
                        </table>
                     </div>
                  </div>
               </div>
            </div>
         </div>
      </main>
   </div>

   <Notification :show="notif.show" :type="notif.type" :message="notif.message" :messageDetails="notif.messageDetails" @closed="notifClosed"/>
   <Loader :show="isLoading" />
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { isLoggedIn, handleApiError, getBackendBaseUrl, getUserToken, logout } from '@/utils/common'
import Agent from '@/composables/admin/agent'
import Loader from '@/widgets/Loader.vue'
import Notification from '@/widgets/Notification.vue'
import { NotificationType } from '@/types/index'
import router from '@/router'
import axios from 'axios'

const auth = isLoggedIn()
const isAgencyAdmin = computed(() => auth && auth.user && auth.user.type === 'agent' && auth.user.sub_type === null)

const isLoading = ref<boolean>(true)
const isDownloading = ref<boolean>(false)
const { reportsStats } = Agent()
const baseUrl = getBackendBaseUrl()

const selectedScope = ref<string>('this_month')
const startDate = ref<string>('')
const endDate = ref<string>('')

const sales = ref<any>({ count: 0, sum: '0.00' })
const paid = ref<any>({ count: 0, sum: '0.00' })
const unpaid = ref<any>({ count: 0, sum: '0.00' })
const staffBreakdown = ref<any[]>([])

const notif = ref<NotificationType>({ show: false, type: 'success', message: '', messageDetails: '' })

const scopes = ref([
   { id: 'all_time', name: 'Lifetime' },
   { id: 'today', name: 'Today' },
   { id: 'this_week', name: 'This Week' },
   { id: 'this_month', name: 'This Month' },
   { id: 'last_month', name: 'Last Month' },
   { id: 'this_year', name: 'This Year' },
   { id: 'last_year', name: 'Last Year' },
   { id: 'custom', name: 'Custom Date Range' }
])

onMounted(async () => {
   await fetchReports()
})

const getFinalScopeString = (): string => {
   if (selectedScope.value === 'custom') {
      if (!startDate.value || !endDate.value) {
         return ''
      }
      const format = (dateStr: string) => {
         const parts = dateStr.split('-')
         return `${parts[2]}-${parts[1]}-${parts[0]}`
      }
      return `custom_${format(startDate.value)}_${format(endDate.value)}`
   }
   return selectedScope.value
}

const fetchReports = async () => {
   const finalScope = getFinalScopeString()
   if (finalScope === '') {
      // Missing custom range date input
      return
   }

   isLoading.value = true
   const response = await reportsStats(finalScope)
   isLoading.value = false

   if (response.status === 200) {
      sales.value = response.data.sales
      paid.value = response.data.paid
      unpaid.value = response.data.unpaid
      staffBreakdown.value = response.data.staff_breakdown || []
   } else if (response.response && response.response.status === 401) {
      logout(router)
   } else {
      const errorMsg = handleApiError(response)
      notif.value = { show: true, type: 'error', message: 'Failed to load', messageDetails: errorMsg }
      setTimeout(() => { notifClosed() }, 5000)
   }
}

const notifClosed = () => {
   notif.value.show = false
}

const downloadCsv = async (type: string, staffId: number = 0, staffName: string = '') => {
   const finalScope = getFinalScopeString()
   if (finalScope === '') {
      notif.value = { show: true, type: 'error', message: 'Date Missing', messageDetails: 'Please select both start and end dates first.' }
      setTimeout(() => { notifClosed() }, 4000)
      return
   }

   try {
      isDownloading.value = true
      isLoading.value = true

      let url = `${baseUrl}api/agent/reports/download/${type}/${finalScope}`
      if (type === 'staff') {
         url += `?sub_agent_id=${staffId}`
      }

      const response = await axios.get(url, {
         headers: {
            'Authorization': `Bearer ${getUserToken()}`
         },
         responseType: 'blob'
      })

      let filename = `Financial_Report_${type.toUpperCase()}_${finalScope}.csv`
      if (type === 'staff') {
         filename = `Performance_Report_${staffName.replace(/\s+/g, '_')}_${finalScope}.csv`
      }

      const disposition = response.headers['content-disposition']
      if (disposition && disposition.indexOf('attachment') !== -1) {
         const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
         const matches = filenameRegex.exec(disposition)
         if (matches != null && matches[1]) { 
            filename = matches[1].replace(/['"]/g, '')
         }
      }

      const blob = new Blob([response.data], { type: 'text/csv' })
      const link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      notif.value = { show: true, type: 'success', message: 'Success', messageDetails: 'Report CSV downloaded successfully' }
      setTimeout(() => { notifClosed() }, 4000)
   } catch (error) {
      notif.value = { show: true, type: 'error', message: 'Download Failed', messageDetails: 'Failed to download report. Please try again.' }
      setTimeout(() => { notifClosed() }, 5000)
      console.error(error)
   } finally {
      isDownloading.value = false
      isLoading.value = false
   }
}
</script>
